import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  type WorkspaceItem,
  WorkspacesService,
  type WorkspaceUpdate,
} from '@bizzdesign/data-access-workspaces';
import { ContentOpenService } from '@bizzdesign/feature-content-open';
import { SplitSearchContainerComponent } from '@bizzdesign/feature-search';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import {
  type ActionButton,
  type BDMenuItem,
  type Item,
} from '@bizzdesign/shared/models';
import { baseRoutes } from '@bizzdesign/shared/utils';
import {
  ItemGridComponent,
  ItemGridLoadingComponent,
} from '@bizzdesign/ui-item-grid';
import {
  ProductAnalyticsService,
  UserActivityTrackingService,
} from '@bizzdesign/shared/services';
import { OnboardingFirstLoginService } from '@bizzdesign/workspaces/feature-onboarding';
import {
  catchError,
  from,
  map,
  type Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

const GET_STARTED_LABEL_THRESHOLD_SECONDS = 120;

@Component({
  selector: 'bd-home-container',
  imports: [
    AsyncPipe,
    NgClass,
    SplitSearchContainerComponent,
    SharedI18nModule,
    ItemGridComponent,
    ItemGridLoadingComponent,
  ],
  templateUrl: './home-container.component.html',
  styleUrl: './home-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeContainerComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly workspacesService = inject(WorkspacesService);
  private readonly productAnalyticsService = inject(ProductAnalyticsService);
  private readonly router = inject(Router);
  private readonly contentOpenService = inject(ContentOpenService);
  private readonly userActivityTrackingService = inject(
    UserActivityTrackingService,
  );
  private readonly onboardingFirstLoginService = inject(
    OnboardingFirstLoginService,
  );
  recentWorkspacesLoading = true;
  isGetStartedUser = signal(false);
  labelDataId = computed(() =>
    this.isGetStartedUser()
      ? 'get-started-workspaces-items-label'
      : 'recent-workspaces-items-label',
  );
  labelKey = computed(() =>
    this.isGetStartedUser()
      ? 'featureHome.getStartedWorkspacesLabel'
      : 'featureHome.recentWorkspacesLabel',
  );

  context$: Observable<string> = this.route.paramMap.pipe(
    map((paramMap) => paramMap.get('context') ?? 'default'),
  );

  private static readonly MAX_RECENT_HOME_CARDS = 3;
  recentWorkspaces$: Observable<Item<WorkspaceItem>[]> = from(
    this.onboardingFirstLoginService.isRecentFirstLogin(
      GET_STARTED_LABEL_THRESHOLD_SECONDS,
    ),
  ).pipe(
    tap((isRecentFirstLogin) => {
      this.isGetStartedUser.set(isRecentFirstLogin);
    }),
    switchMap(() => this.userActivityTrackingService.recentWorkspaces()),
    switchMap(() =>
      this.workspacesService.getWorkspaceItems({
        pageSize: HomeContainerComponent.MAX_RECENT_HOME_CARDS,
        orderBy: 'lastOpened',
      }),
    ),
    tap(() => {
      this.recentWorkspacesLoading = false;
    }),
    map((page) =>
      page.results.map((workspaceItem) => ({
        data: workspaceItem,
        ...workspaceItem,
        actionButton: {
          labelKey: 'workspaces.openWorkspace',
          tScope: 'featureWorkspaces',
          type: 'asLink',
          group: 'neutral',
          onClick: () => {
            this.onboardingFirstLoginService.dismissGetStarted();
            this.isGetStartedUser.set(false);
            this.productAnalyticsService.sendEvent('recent_workspace_opened');
            this.onWorkspaceOpen({
              ...workspaceItem,
              data: workspaceItem,
            });
          },
        } satisfies ActionButton,
        menuOptions: this.createMenuOptionsForWorkspaceItem(workspaceItem),
      })),
    ),
    catchError(() => {
      this.recentWorkspacesLoading = false;
      return of([]);
    }),
  );

  onWorkspaceUpdate(workspaceUpdate: WorkspaceUpdate) {
    this.workspacesService.updateWorkspace({
      ...workspaceUpdate,
      archived: true,
    });
    this.router.navigate([baseRoutes.archive]);
  }

  onWorkspaceOpen(workspaceItem: Item<WorkspaceItem>) {
    this.contentOpenService.openWorkspace(workspaceItem.id, workspaceItem.name);
  }

  createMenuOptionsForWorkspaceItem(
    workspaceItem: WorkspaceItem,
  ): BDMenuItem[] {
    return [
      {
        labelKey: 'menu.archiveWorkspace',
        tScope: 'uiComponents',
        command: () => {
          this.onWorkspaceUpdate(workspaceItem);
        },
        icon: 'ui-archive',
        subject: 'workspaces',
        actionKey: 'archive',
      },
    ];
  }
}
